import { Link } from "react-router";
import { css } from "styled-system/css";
import { ak } from "./admin-styles";
import { Banner, PageHead, Section } from "~/components/editorial";
import { useIsAdmin } from "~/lib/auth";

interface Entry {
  to: string;
  name: string;
  description: string;
}

const CONNECTIONS: Entry[] = [
  {
    to: "/settings/github",
    name: "GitHub",
    description:
      "Connect GitHub organizations, watch sync health and pick the repos that count as deploys.",
  },
  {
    to: "/settings/jira",
    name: "Jira",
    description: "Connect Jira instances and choose which projects sync.",
  },
  {
    to: "/settings/cursor",
    name: "Cursor",
    description: "Pull AI usage and team spend from the Cursor admin API.",
  },
  {
    to: "/settings/slack",
    name: "Slack digest",
    description: "Schedule the weekly engineering briefing to a Slack channel.",
  },
];

const HOUSEKEEPING: Entry[] = [
  {
    to: "/settings/developers",
    name: "Contributor identities",
    description:
      "Link GitHub and Jira profiles, merge duplicates and attach commit emails.",
  },
  {
    to: "/settings/products",
    name: "Products",
    description:
      "Map repos to products and set each product's tier for the delivery report.",
  },
  {
    to: "/settings/board-reporting",
    name: "Board reporting",
    description: "The PD Schedule board used for leadership reporting.",
  },
  {
    to: "/tools",
    name: "Tools tracker",
    description:
      "Every tool and service the teams use, with access, owners and renewal dates.",
  },
];

export function SettingsPage() {
  const isAdmin = useIsAdmin();
  return (
    <div className={ak.page}>
      <PageHead
        kicker="Admin"
        title="Settings"
        dek="Where Aura gets its data, and the records that keep it honest."
      />
      {!isAdmin && (
        <Banner tone="caution">
          You can look around, but only Aura admins can change connections,
          syncs, identities and product mapping. Admins are the members of the
          Okta admin group.
        </Banner>
      )}

      <Section kicker="I. Connections">
        <Directory entries={CONNECTIONS} start={1} />
      </Section>

      <Section kicker="II. Housekeeping">
        <Directory entries={HOUSEKEEPING} start={CONNECTIONS.length + 1} />
      </Section>
    </div>
  );
}

function Directory({ entries, start }: { entries: Entry[]; start: number }) {
  return (
    <ol className={styles.list}>
      {entries.map((entry, i) => (
        <li key={entry.to}>
          <Link to={entry.to} className={styles.item}>
            <span className={styles.index}>
              {String(start + i).padStart(2, "0")}
            </span>
            <span>
              <span className={styles.name}>{entry.name}</span>
              <span className={styles.description}>{entry.description}</span>
            </span>
            <span className={styles.arrow} aria-hidden>
              →
            </span>
          </Link>
        </li>
      ))}
    </ol>
  );
}

const styles = {
  list: css({ listStyle: "none", m: 0, p: 0 }),
  item: css({
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1fr) 20px",
    alignItems: "baseline",
    gap: "8px",
    py: "12px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
    color: "ed.ink",
    textDecoration: "none",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.panel" },
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "ed.ink",
      outlineOffset: "-2px",
    },
  }),
  index: css({
    fontFamily: "mono",
    fontSize: "12px",
    color: "ed.ink3",
    pl: "2px",
  }),
  name: css({
    display: "block",
    fontFamily: "serif",
    fontSize: "19px",
    fontWeight: 700,
    lineHeight: 1.25,
  }),
  description: css({
    display: "block",
    mt: "2px",
    fontFamily: "sans",
    fontSize: "13px",
    lineHeight: 1.45,
    color: "ed.ink2",
  }),
  arrow: css({ fontFamily: "sans", color: "ed.ink3", textAlign: "right" }),
};
